import React, { useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import useTranslation from "../../i18n/useTranslation";

const PASSWORD_RESET_MUTATION = gql`
    mutation PasswordReset($token: String!, $newPassword1: String!, $newPassword2: String!) {
        passwordReset(token: $token, newPassword1: $newPassword1, newPassword2: $newPassword2) {
            success
            errors
        }
    }
`;

const PasswordReset = () => {
    const { t } = useTranslation();
    const { token } = useParams();

    const [newPassword1, setNewPassword1] = useState("");
    const [newPassword2, setNewPassword2] = useState("");
    const [status, setStatus] = useState("");
    const [success, setSuccess] = useState(null);
    const [errors, setErrors] = useState([]);

    const [passwordReset, { loading }] = useMutation(PASSWORD_RESET_MUTATION);

    useEffect(() => {
        const prevTitle = document.title;
        document.title = t("passwordReset.meta.title");

        return () => {
            document.title = prevTitle;
        };
    }, [t]);

    const parseErrors = (errorsObj) => {
        return Object.values(errorsObj)
            .flat()
            .map((err) => err.message || err);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors([]);
        setStatus("");

        if (!newPassword1 || !newPassword2) {
            setErrors([t("passwordReset.errors.enterAndConfirmPassword")]);
            return;
        }

        if (newPassword1 !== newPassword2) {
            setErrors([t("passwordReset.errors.passwordsDoNotMatch")]);
            return;
        }

        passwordReset({ variables: { token, newPassword1, newPassword2 } })
            .then((res) => {
                if (res?.data?.passwordReset?.success) {
                    setSuccess(true);
                    setStatus(t("passwordReset.status.success"));
                } else {
                    const receivedErrors = parseErrors(
                        res?.data?.passwordReset?.errors || {}
                    );
                    setErrors(
                        receivedErrors.length > 0
                            ? receivedErrors
                            : [t("passwordReset.errors.unknownError")]
                    );
                    setSuccess(false);
                }
            })
            .catch(() => {
                setErrors([t("passwordReset.errors.somethingWentWrong")]);
                setSuccess(false);
            });
    };

    return (
        <div
            style={{
                backgroundColor: "#14151a",
                color: "#ffffff",
                fontFamily: "'Poppins', sans-serif",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflowX: "hidden",
                padding: 20,
            }}
        >
            <div
                style={{
                    backgroundColor: "#1f1f2e",
                    borderRadius: "12px",
                    padding: "30px",
                    boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.3)",
                    textAlign: "center",
                    maxWidth: "450px",
                    width: "100%",
                }}
            >
                <div className="mb-3">
                    <NavLink to="/">
                        <img
                            src="/dist/img/angeonx-sports.png"
                            alt={t("passwordReset.brand.logoAlt")}
                            className="logo img-fluid"
                            style={{ height: "80px", marginBottom: "10px" }}
                        />
                    </NavLink>

                    <h2
                        style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "var(--kbs-primary, #00adff)",
                        }}
                    >
                        {t("passwordReset.brand.namePrimary")}{" "}
                        <span style={{ color: "#ffffff" }}>
                            {t("passwordReset.brand.nameSecondary")}
                        </span>
                    </h2>
                </div>

                <h3
                    className="mb-3"
                    style={{ fontSize: "20px", fontWeight: "bold" }}
                >
                    {t("passwordReset.content.title")}
                </h3>

                {success !== null && (
                    <p>
                        {success ? (
                            <FaCheckCircle size={50} color="var(--kbs-primary, #00adff)" />
                        ) : (
                            <FaExclamationTriangle size={50} color="red" />
                        )}
                    </p>
                )}

                {errors.length > 0 && (
                    <div
                        className="alert alert-danger alert-dismissible"
                        style={{ marginTop: "20px", textAlign: "left" }}
                    >
                        <button
                            type="button"
                            className="close"
                            data-dismiss="alert"
                            aria-hidden="true"
                            style={{ color: "#fff" }}
                        >
                            &times;
                        </button>

                        <h5 style={{ fontSize: "18px", fontWeight: "bold" }}>
                            <i className="icon fas fa-ban"></i> {t("passwordReset.errors.title")}
                        </h5>

                        <ul style={{ paddingLeft: "20px", margin: "10px 0" }}>
                            {errors.map((msg, index) => (
                                <li
                                    key={index}
                                    style={{
                                        listStyleType: "disc",
                                        marginBottom: "5px",
                                        fontSize: "16px",
                                    }}
                                >
                                    {msg}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {success === true ? (
                    <p
                        className="mt-3"
                        style={{
                            fontSize: "16px",
                            fontWeight: "500",
                            color: "#f1f2f5",
                        }}
                    >
                        {status}
                    </p>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        style={{ textAlign: "left", marginTop: "20px" }}
                    >
                        <div className="form-group mb-3">
                            <label style={{ fontWeight: "500", marginBottom: "5px" }}>
                                {t("passwordReset.fields.newPassword")}
                            </label>
                            <input
                                type="password"
                                className="form-control"
                                value={newPassword1}
                                onChange={(e) => setNewPassword1(e.target.value)}
                                style={{
                                    backgroundColor: "#2a2a3d",
                                    color: "#ffffff",
                                    border: "1px solid #444",
                                    borderRadius: "8px",
                                    padding: "10px",
                                    width: "100%",
                                }}
                                required
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label style={{ fontWeight: "500", marginBottom: "5px" }}>
                                {t("passwordReset.fields.confirmNewPassword")}
                            </label>
                            <input
                                type="password"
                                className="form-control"
                                value={newPassword2}
                                onChange={(e) => setNewPassword2(e.target.value)}
                                style={{
                                    backgroundColor: "#2a2a3d",
                                    color: "#ffffff",
                                    border: "1px solid #444",
                                    borderRadius: "8px",
                                    padding: "10px",
                                    width: "100%",
                                }}
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                backgroundColor: "var(--kbs-primary, #00adff)",
                                color: "#14151a",
                                fontWeight: "bold",
                                fontSize: "16px",
                                padding: "10px 20px",
                                borderRadius: "8px",
                                border: "none",
                                width: "100%",
                                cursor: "pointer",
                            }}
                        >
                            {loading
                                ? t("passwordReset.buttons.resetting")
                                : t("passwordReset.buttons.resetPassword")}
                        </button>

                        {status && success === true && (
                            <p
                                className="mt-3"
                                style={{ fontSize: "14px", color: "#f1f2f5" }}
                            >
                                {status}
                            </p>
                        )}
                    </form>
                )}
            </div>
        </div>
    );
};

export default PasswordReset;
